import moment from "moment";

const IST_TIMEZONE_OFFSET = 330;
const DANGEROUS_CELL_PREFIX = /^[=+\-@]/;

const asArray = (value) => (Array.isArray(value) ? value : []);

const safeText = (value, fallback = "-") => {
  if (value === 0) return "0";
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (value == null) return fallback;

  const text =
    typeof value === "object" ? JSON.stringify(value) : String(value).trim();
  if (!text) return fallback;
  return DANGEROUS_CELL_PREFIX.test(text) ? `'${text}` : text;
};

const formatDate = (value, includeTime = false) => {
  if (!value) return "-";
  const parsed = moment(value);
  if (!parsed.isValid()) return "-";
  return parsed
    .utcOffset(IST_TIMEZONE_OFFSET)
    .format(includeTime ? "DD/MM/YYYY hh:mm A" : "DD/MM/YYYY");
};

const formatAddress = (address = {}) =>
  [
    address?.unitNumber,
    address?.address1,
    address?.address2,
    address?.landmark,
    address?.city,
    address?.district,
    address?.state,
    address?.country,
    address?.pincode,
  ]
    .map((part) => safeText(part, ""))
    .filter(Boolean)
    .join(", ") || "-";

const getName = (profile = {}) => {
  const nameInfo = profile?.personalInfo?.basicInfo?.nameInfo || {};
  return [nameInfo?.title, nameInfo?.fullName]
    .map((part) => safeText(part, ""))
    .filter(Boolean)
    .join(" ") || "-";
};

const getOtherInfo = (profile = {}) =>
  profile?.personalInfo?.basicInfo?.otherInfo || {};

const getContactPerson = (profile = {}) => {
  const contact = profile?.contactPersonInfo?.basicInfo || {};
  const nameInfo = contact?.nameInfo || {};
  const otherInfo = contact?.otherInfo || {};
  return {
    name:
      [nameInfo?.title, nameInfo?.fullName]
        .map((part) => safeText(part, ""))
        .filter(Boolean)
        .join(" ") || "-",
    phone: safeText(otherInfo?.phone || contact?.phone),
  };
};

const normalizeFilePart = (value, fallback) =>
  String(value || fallback)
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase() || fallback;

const loadExcelJS = async () => {
  const excelModule = await import("exceljs");
  return excelModule.default || excelModule;
};

const styleSheet = (worksheet, headerRowNumber = 1) => {
  worksheet.views = [{ state: "frozen", ySplit: headerRowNumber }];
  worksheet.autoFilter = {
    from: { row: headerRowNumber, column: 1 },
    to: { row: headerRowNumber, column: worksheet.columnCount },
  };

  const header = worksheet.getRow(headerRowNumber);
  header.height = 30;
  header.font = { bold: true, color: { argb: "FFFFFFFF" } };
  header.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF36546F" },
  };
  header.alignment = { vertical: "middle", horizontal: "center", wrapText: true };

  for (let rowNumber = headerRowNumber + 1; rowNumber <= worksheet.rowCount; rowNumber += 1) {
    const row = worksheet.getRow(rowNumber);
    row.alignment = { vertical: "top", wrapText: true };
    if (rowNumber % 2 === 0) {
      row.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFF7FAFC" },
        };
      });
    }
  }
};

const addSheet = (workbook, name, columns, rows) => {
  const worksheet = workbook.addWorksheet(name);
  worksheet.columns = columns.map(({ header, key, width = 18 }) => ({
    header,
    key,
    width,
  }));
  rows.forEach((row) => worksheet.addRow(row));
  styleSheet(worksheet);
  return worksheet;
};

const addSummarySheet = (workbook, title, metrics, notes) => {
  const worksheet = workbook.addWorksheet("Summary");
  worksheet.columns = [
    { header: "Metric", key: "metric", width: 34 },
    { header: "Value", key: "value", width: 28 },
  ];
  metrics.forEach(([metric, value]) => worksheet.addRow({ metric, value }));
  if (notes.length) {
    worksheet.addRow({});
    worksheet.addRow({ metric: "Notes", value: notes.join(" | ") });
  }
  styleSheet(worksheet);
  worksheet.getCell("A1").value = title;
  worksheet.mergeCells("A1:B1");
  worksheet.insertRow(2, ["Metric", "Value"]);
  styleSheet(worksheet, 2);
  worksheet.views = [{ state: "frozen", ySplit: 2 }];
  return worksheet;
};

const saveWorkbook = async (workbook, fileName) => {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const PATIENT_MASTER_COLUMNS = [
  { header: "MRN", key: "mrn", width: 18 },
  { header: "Name", key: "name", width: 28 },
  { header: "Gender", key: "gender", width: 12 },
  { header: "DOB", key: "dob", width: 14 },
  { header: "Phone", key: "phone", width: 16 },
  { header: "Address", key: "address", width: 45 },
  { header: "Contact Person", key: "contactPerson", width: 26 },
  { header: "Contact Phone", key: "contactPhone", width: 16 },
  { header: "Onboarded On", key: "createdAt", width: 18 },
];

export const downloadPatientDataExcel = async ({ patients = [], sourceLabel = "partner-patients" } = {}) => {
  if (!asArray(patients).length) {
    const error = new Error("No patient records are available to export.");
    error.code = "NO_EXPORT_DATA";
    throw error;
  }

  const ExcelJS = await loadExcelJS();
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Ceviliyar";
  workbook.created = new Date();

  const masterRows = asArray(patients).map((account) => {
    const otherInfo = getOtherInfo(account);
    const contact = getContactPerson(account);

    return {
      mrn: safeText(account?.mrn),
      name: getName(account),
      gender: safeText(otherInfo?.gender),
      dob: formatDate(otherInfo?.dob),
      phone: safeText(account?.phone || otherInfo?.phone),
      address: formatAddress(account?.personalInfo?.addressInfo),
      contactPerson: contact.name,
      contactPhone: contact.phone,
      createdAt: formatDate(account?.createdAt, true),
    };
  });

  addSheet(workbook, "Patient Master", PATIENT_MASTER_COLUMNS, masterRows);

  const fileName = `${normalizeFilePart(sourceLabel, "partner-patients")}-data-${moment().format("YYYYMMDD-HHmmss")}.xlsx`;
  await saveWorkbook(workbook, fileName);
  return { count: masterRows.length, fileName };
};

const CARE_TEAM_BASE_COLUMNS = [
  { header: "Name", key: "name", width: 28 },
  { header: "Role", key: "role", width: 20 },
];

export const downloadCareTeamDataExcel = async ({ careTeam = [], sourceLabel = "partner-care-team" } = {}) => {
  const members = asArray(careTeam).filter((member) => member?.role !== "patient");
  if (!members.length) {
    const error = new Error("No care-team records are available to export.");
    error.code = "NO_EXPORT_DATA";
    throw error;
  }

  const ExcelJS = await loadExcelJS();
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Ceviliyar";
  workbook.created = new Date();

  const masterRows = [];
  members.forEach((member) => {
    const otherInfo = getOtherInfo(member);
    const common = {
      name: getName(member),
      role: safeText(member?.subRole || member?.role),
    };

    masterRows.push({
      ...common,
      active: safeText(member?.active),
      phone: safeText(member?.phone || otherInfo?.phone),
      email: safeText(otherInfo?.email),
      gender: safeText(otherInfo?.gender),
      dob: formatDate(otherInfo?.dob),
      address: formatAddress(member?.personalInfo?.addressInfo),
      onboardedOn: formatDate(member?.createdAt, true),
    });
  });

  addSummarySheet(
    workbook,
    "Ceviliyar Care Team Data Export",
    [
      ["Generated At (IST)", formatDate(new Date(), true)],
      ["Care Team Members", members.length],
      ["Nurses", members.filter((member) => member?.role === "nurse").length],
      ["Physiotherapists", members.filter((member) => member?.role === "physiotherapist").length],
    ],
    [],
  );
  addSheet(workbook, "Care Team Master", [...CARE_TEAM_BASE_COLUMNS, { header: "Active", key: "active", width: 11 }, { header: "Phone", key: "phone", width: 16 }, { header: "Email", key: "email", width: 28 }, { header: "Gender", key: "gender", width: 12 }, { header: "DOB", key: "dob", width: 14 }, { header: "Address", key: "address", width: 45 }, { header: "Onboarded On", key: "onboardedOn", width: 20 }], masterRows);

  const fileName = `${normalizeFilePart(sourceLabel, "partner-care-team")}-data-${moment().format("YYYYMMDD-HHmmss")}.xlsx`;
  await saveWorkbook(workbook, fileName);
  return { count: members.length, fileName };
};

const ADMIN_BOOKING_COLUMNS = [
  { header: "Booking ID", key: "bookingId", width: 24 },
  { header: "Visit ID", key: "visitId", width: 24 },
  { header: "Patient Name", key: "patientName", width: 28 },
  { header: "Status", key: "status", width: 20 },
  { header: "Booking Created", key: "bookingCreated", width: 20 },
  { header: "Service Start", key: "serviceStart", width: 16 },
  { header: "Service End", key: "serviceEnd", width: 16 },
  { header: "Duty Type", key: "dutyType", width: 16 },
  { header: "Shift / Slot", key: "slot", width: 20 },
  { header: "Specialist Role", key: "specialistRole", width: 20 },
  { header: "Assigned HCP", key: "assignedHcp", width: 28 },
  { header: "Standby HCP", key: "standbyHcp", width: 28 },
  { header: "Booked By", key: "bookedBy", width: 16 },
  { header: "Services", key: "services", width: 40 },
  { header: "Address", key: "address", width: 48 },
  { header: "Payment Mode", key: "paymentMode", width: 18 },
  { header: "Payment Status", key: "paymentStatus", width: 18 },
  { header: "Payment ID(s)", key: "paymentIds", width: 42 },
  { header: "Payment Reference / QR ID(s)", key: "paymentReferences", width: 42 },
  { header: "Payment Date", key: "paymentDate", width: 20 },
  { header: "Patient Payment Received", key: "patientPaymentReceived", width: 22 },
  { header: "Gateway Deduction", key: "gatewayDeduction", width: 18 },
  { header: "Ceviliyar Platform Fee", key: "ceviliyarPlatformFee", width: 22 },
  { header: "Partner Net Payout", key: "partnerNetPayout", width: 20 },
  { header: "Processed Transfer Total", key: "processedTransferTotal", width: 24 },
  { header: "Pending Transfer Total", key: "pendingTransferTotal", width: 22 },
  { header: "Duty Days", key: "dutyDays", width: 12 },
  { header: "Timeline Events", key: "timelineEvents", width: 16 },
];

const ADMIN_DUTY_COLUMNS = [
  { header: "Booking ID", key: "bookingId", width: 24 },
  { header: "Visit ID", key: "visitId", width: 24 },
  { header: "Patient Name", key: "patientName", width: 28 },
  { header: "Day", key: "day", width: 10 },
  { header: "Duty Date", key: "dutyDate", width: 16 },
  { header: "Duty Type", key: "dutyType", width: 16 },
  { header: "Service Status", key: "serviceStatus", width: 18 },
  { header: "Scheduled Start", key: "scheduledStart", width: 20 },
  { header: "Scheduled End", key: "scheduledEnd", width: 20 },
  { header: "Check-in Verified", key: "checkInVerified", width: 18 },
  { header: "Check-in Time", key: "checkInTime", width: 20 },
  { header: "Check-out Verified", key: "checkOutVerified", width: 18 },
  { header: "Check-out Time", key: "checkOutTime", width: 20 },
  { header: "Daily Payment Status", key: "dailyPaymentStatus", width: 22 },
  { header: "Daily Payment Amount", key: "dailyPaymentAmount", width: 22 },
  { header: "Payment ID", key: "paymentId", width: 34 },
  { header: "Payment QR Code ID", key: "paymentQrCodeId", width: 34 },
  { header: "Daily Payment Date", key: "dailyPaymentDate", width: 20 },
  { header: "Total Patient Payment", key: "patientPaid", width: 22 },
  { header: "Payment Gateway Fee", key: "gatewayDeductions", width: 22 },
  { header: "Ceviliyar Platform Fee", key: "companyTotal", width: 22 },
  { header: "Ceviliyar Net Commission", key: "companyNetCommission", width: 24 },
  { header: "Ceviliyar Routing Fee", key: "companyRoutingFee", width: 22 },
  { header: "Ceviliyar Transfer ID", key: "companyTransferId", width: 34 },
  { header: "Ceviliyar Transfer Status", key: "companyTransferStatus", width: 24 },
  { header: "Ceviliyar Transfer Date", key: "companyTransferDate", width: 22 },
  { header: "Gross Hospital Share", key: "partnerGrossShare", width: 22 },
  { header: "Automated Transfer Fee", key: "partnerTransferCharge", width: 22 },
  { header: "Net Payout to Hospital", key: "partnerNetReceives", width: 24 },
  { header: "Partner Amount To Transfer", key: "partnerAmountToTransfer", width: 26 },
  { header: "Partner Bonus", key: "partnerBonus", width: 16 },
  { header: "Partner Penalty", key: "partnerPenalty", width: 18 },
  { header: "Partner Transfer ID", key: "partnerTransferId", width: 34 },
  { header: "Partner Transfer Status", key: "partnerTransferStatus", width: 24 },
  { header: "Partner Transfer Date", key: "partnerTransferDate", width: 22 },
  { header: "Transfer Reconciliation", key: "transferReconciliation", width: 24 },
];

const ADMIN_SERVICE_COLUMNS = [
  { header: "Booking ID", key: "bookingId", width: 24 },
  { header: "Visit ID", key: "visitId", width: 24 },
  { header: "Patient Name", key: "patientName", width: 28 },
  { header: "Duty Date", key: "dutyDate", width: 16 },
  { header: "Day", key: "day", width: 10 },
  { header: "Service", key: "service", width: 24 },
  { header: "Reported At", key: "reportedAt", width: 20 },
  { header: "Details", key: "details", width: 60 },
];

const ADMIN_TIMELINE_COLUMNS = [
  { header: "Booking ID", key: "bookingId", width: 24 },
  { header: "Visit ID", key: "visitId", width: 24 },
  { header: "Patient Name", key: "patientName", width: 28 },
  { header: "Event", key: "event", width: 24 },
  { header: "When", key: "when", width: 20 },
  { header: "Description", key: "description", width: 60 },
  { header: "Actor", key: "actor", width: 28 },
];

const getAdminId = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  return String(value?._id || value?.id || value?.$oid || value?.value || "");
};

const joinAdminValues = (values) => {
  const normalized = values
    .map(getAdminId)
    .map((value) => value.trim())
    .filter(Boolean);
  return Array.from(new Set(normalized)).join(", ") || "-";
};

const getAdminName = (profile = {}) => {
  const nameInfo = profile?.personalInfo?.basicInfo?.nameInfo || {};
  return [nameInfo?.title, nameInfo?.fullName]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ");
};

const getAdminVisitId = (visit) => getAdminId(visit?._id || visit?.id);

const getAdminPatientName = (visit) =>
  String(
    visit?.patient?.name ||
      getAdminName(visit?.patientInfo) ||
      visit?.patientInfo?.name ||
      "Unknown Patient",
  ).trim() || "Unknown Patient";

const getAdminAssignedHcp = (visit, duty = null) => {
  const nameInfo = duty?.attendedBy?.userID?.personalInfo?.basicInfo?.nameInfo;
  const populatedName = [nameInfo?.title, nameInfo?.fullName]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ");
  return (
    populatedName ||
    visit?.dutyStaff?.name ||
    visit?.specialistInfo?.name ||
    visit?.assessmentStaff?.name ||
    "Unassigned"
  );
};

const getAdminAddress = (visit) => {
  const address = visit?.visitInfo?.addressInfo || {};
  return (
    [
      address?.address1,
      address?.address2,
      address?.landmark,
      address?.city,
      address?.district,
      address?.state,
      address?.pincode,
    ]
      .map((part) => String(part || "").trim())
      .filter(Boolean)
      .join(", ") || "-"
  );
};

const getAdminServices = (visit) =>
  asArray(visit?.visitInfo?.services)
    .map((service) =>
      typeof service === "string"
        ? service
        : service?.name || service?.service || service?.serviceType || "",
    )
    .map((service) => String(service || "").trim())
    .filter(Boolean)
    .join(", ") || "-";

const getAdminPaymentDetails = (visit) => {
  const paymentInfo = visit?.paymentInfo || {};
  const ids = [
    paymentInfo?.paidInfo?.metadata?.razorpay_payment_id,
    paymentInfo?.paidInfo?.paymentID,
    paymentInfo?.paymentID,
    paymentInfo?.assessment?.paidInfo?.metadata?.razorpay_payment_id,
    paymentInfo?.assessment?.paidInfo?.paymentID,
    paymentInfo?.assessment?.paymentID,
    paymentInfo?.locationQr?.paymentID,
    ...asArray(paymentInfo?.sessions).flatMap((session) => [
      session?.paidInfo?.metadata?.razorpay_payment_id,
      session?.paidInfo?.paymentID,
      session?.paymentID,
    ]),
  ];
  const references = [
    paymentInfo?.paidInfo?.metadata?.razorpay_payment_link_reference_id,
    paymentInfo?.assessment?.paidInfo?.metadata?.razorpay_payment_link_reference_id,
    paymentInfo?.locationQr?.qrCodeID,
    ...asArray(paymentInfo?.sessions).flatMap((session) => [
      session?.qrCodeID,
      session?.paidInfo?.metadata?.razorpay_payment_link_reference_id,
    ]),
  ];
  const status =
    paymentInfo?.locationQr?.status ||
    (paymentInfo?.paidInfo?.paid || paymentInfo?.assessment?.paidInfo?.paid
      ? "paid"
      : "-");
  const paidDate =
    paymentInfo?.paidInfo?.paidDateTime ||
    paymentInfo?.assessment?.paidInfo?.paidDateTime ||
    paymentInfo?.locationQr?.paidDateTime ||
    null;
  const amount =
    paymentInfo?.paidInfo?.amountPaid ??
    paymentInfo?.assessment?.paidInfo?.amountPaid ??
    paymentInfo?.grandTotal ??
    paymentInfo?.assessment?.grandTotal ??
    paymentInfo?.locationQr?.amount ??
    null;

  return {
    ids: joinAdminValues(ids),
    references: joinAdminValues(references),
    status: safeText(status),
    paidDate,
    amount,
  };
};

const getAdminDutyPayout = (duty) => {
  const payout = duty?.payout || {};
  const company = payout?.commisionToTheCompany || {};
  const partner = payout?.payoutToTheVendor || {};
  return { payout, company, partner };
};

const getAdminDutyPayment = (duty) => ({
  status: safeText(duty?.payment?.status),
  amount: duty?.payment?.amount,
  paymentId: safeText(duty?.payment?.paymentID),
  qrCodeId: safeText(duty?.payment?.qrCodeID),
  paidDate: duty?.payment?.paidDateTime,
});

const getAdminTransferStatus = (duty, partner) => {
  const status = String(partner?.status || "").trim().toLowerCase();
  const partnerAmount = partner?.amountToTransfer ?? partner?.netPayout;
  const completed =
    duty?.endDuty?.verified === true ||
    ["completed", "endduty", "closed", "forceclosed"].includes(
      String(duty?.serviceStatus || "").trim().toLowerCase().replace(/[\s_-]+/g, ""),
    );

  if (status === "processed") return "Processed";
  if (!completed) {
    return status === "pending" && Number(partnerAmount) > 0
      ? "Not yet eligible"
      : "Duty not completed";
  }
  if (partnerAmount == null && partner?.netPayout == null) return "Missing payout data";
  if (status === "pending" && Number(partnerAmount) > 0) return "Pending transfer";
  if (status === "pending") return "No transfer required";
  return safeText(partner?.status, "Transfer status unavailable");
};

const getAdminDutyRows = (visits, getDutyTransactionSummary) => {
  const rows = [];
  asArray(visits).forEach((visit) => {
    const bookingId = safeText(visit?.jobID || getAdminVisitId(visit));
    const visitId = safeText(getAdminVisitId(visit));
    const patientName = safeText(getAdminPatientName(visit));
    asArray(visit?.dutyInfo).forEach((duty, dutyIndex) => {
      const { company, partner } = getAdminDutyPayout(duty);
      const transaction = getDutyTransactionSummary(visit, duty);
      const dailyPayment = getAdminDutyPayment(duty);
      rows.push({
        bookingId,
        visitId,
        patientName,
        day: safeText(duty?.day || dutyIndex + 1),
        dutyDate: safeText(duty?.date),
        dutyType: safeText(duty?.type || visit?.dutyType),
        serviceStatus: safeText(duty?.serviceStatus),
        scheduledStart: formatDate(duty?.startTime, true),
        scheduledEnd: formatDate(duty?.endTime, true),
        checkInVerified: safeText(duty?.startDuty?.verified),
        checkInTime: formatDate(duty?.startDuty?.time, true),
        checkOutVerified: safeText(duty?.endDuty?.verified),
        checkOutTime: formatDate(duty?.endDuty?.time, true),
        dailyPaymentStatus: dailyPayment.status,
        dailyPaymentAmount: safeText(dailyPayment.amount),
        paymentId: dailyPayment.paymentId,
        paymentQrCodeId: dailyPayment.qrCodeId,
        dailyPaymentDate: formatDate(dailyPayment.paidDate, true),
        patientPaid: safeText(transaction.patientPaid),
        gatewayDeductions: safeText(transaction.gatewayDeductions),
        companyTotal: safeText(transaction.companyTotal),
        companyNetCommission: safeText(company?.netCommission),
        companyRoutingFee: safeText(company?.routingFee),
        companyTransferId: safeText(company?.transferID),
        companyTransferStatus: safeText(company?.status),
        companyTransferDate: safeText(company?.transferDateTime),
        partnerGrossShare: safeText(transaction.partnerGrossShare),
        partnerTransferCharge: safeText(transaction.partnerTransferCharge),
        partnerNetReceives: safeText(transaction.partnerNetReceives),
        partnerAmountToTransfer: safeText(partner?.amountToTransfer),
        partnerBonus: safeText(partner?.bonus),
        partnerPenalty: safeText(partner?.penalty),
        partnerTransferId: safeText(partner?.transferID),
        partnerTransferStatus: safeText(partner?.status),
        partnerTransferDate: safeText(partner?.transferDateTime),
        transferReconciliation: getAdminTransferStatus(duty, partner),
      });
    });
  });
  return rows;
};

const getAdminBookingRows = (visits, dutyRows, getDutyTransactionSummary) => {
  const dutyRowsByVisit = new Map();
  dutyRows.forEach((row) => {
    const visitRows = dutyRowsByVisit.get(row.visitId) || [];
    visitRows.push(row);
    dutyRowsByVisit.set(row.visitId, visitRows);
  });

  return asArray(visits).map((visit) => {
    const payment = getAdminPaymentDetails(visit);
    const visitId = getAdminVisitId(visit);
    const visitDutyRows = dutyRowsByVisit.get(visitId) || [];
    const summary = visitDutyRows.reduce(
      (totals, row) => {
        totals.company += Number(row.companyTotal) || 0;
        totals.partner += Number(row.partnerNetReceives) || 0;
        const reconciliation = row.transferReconciliation;
        if (reconciliation === "Processed") totals.processed += Number(row.partnerNetReceives) || 0;
        if (reconciliation === "Pending transfer") totals.pending += Number(row.partnerNetReceives) || 0;
        return totals;
      },
      { company: 0, partner: 0, processed: 0, pending: 0 },
    );
    const paymentSummary = visitDutyRows.reduce(
      (totals, row) => {
        totals.received += Number(row.patientPaid) || 0;
        totals.gateway += Number(row.gatewayDeductions) || 0;
        return totals;
      },
      { received: 0, gateway: 0 },
    );
    const services = getAdminServices(visit);
    return {
      bookingId: safeText(visit?.jobID || visitId),
      visitId: safeText(visitId),
      patientName: safeText(getAdminPatientName(visit)),
      status: safeText(visit?.status),
      bookingCreated: formatDate(visit?.createdAt, true),
      serviceStart: formatDate(visit?.visitInfo?.startDate),
      serviceEnd: formatDate(visit?.visitInfo?.endDate),
      dutyType: safeText(visit?.dutyType),
      slot: safeText(visit?.visitInfo?.shiftType || visit?.visitInfo?.slot),
      specialistRole: safeText(visit?.specialistInfo?.role),
      assignedHcp: safeText(getAdminAssignedHcp(visit)),
      standbyHcp: safeText(visit?.standBySpecialist?.name),
      bookedBy: safeText(visit?.bookedBy),
      services: safeText(services),
      address: safeText(getAdminAddress(visit)),
      paymentMode: safeText(visit?.paymentMode),
      paymentStatus: payment.status,
      paymentIds: payment.ids,
      paymentReferences: payment.references,
      paymentDate: formatDate(payment.paidDate, true),
      patientPaymentReceived: safeText(paymentSummary.received || payment.amount),
      gatewayDeduction: safeText(paymentSummary.gateway || null),
      ceviliyarPlatformFee: safeText(summary.company || null),
      partnerNetPayout: safeText(summary.partner || null),
      processedTransferTotal: safeText(summary.processed || null),
      pendingTransferTotal: safeText(summary.pending || null),
      dutyDays: visitDutyRows.length,
      timelineEvents: asArray(visit?.timeline).length,
    };
  });
};

export const downloadAdminPartnerDataExcel = async ({
  partner = null,
  careTeam = [],
  patients = [],
  bookings = [],
  sourceLabel = "admin-partner",
} = {}) => {
  const [ExcelJS, bookingExport] = await Promise.all([
    loadExcelJS(),
    import("./bookingDetailedExport"),
  ]);
  const exportBookings = bookingExport.filterExportableBookings(bookings);
  const dutyRows = getAdminDutyRows(
    exportBookings,
    bookingExport.getDutyTransactionSummary,
  );
  const bookingRows = getAdminBookingRows(
    exportBookings,
    dutyRows,
    bookingExport.getDutyTransactionSummary,
  );
  const serviceRows = [];
  const timelineRows = [];

  exportBookings.forEach((visit) => {
    const bookingId = safeText(visit?.jobID || getAdminVisitId(visit));
    const visitId = safeText(getAdminVisitId(visit));
    const patientName = safeText(getAdminPatientName(visit));
    asArray(visit?.dutyInfo).forEach((duty, dutyIndex) => {
      asArray(duty?.services).forEach((service, serviceIndex) => {
        serviceRows.push({
          bookingId,
          visitId,
          patientName,
          dutyDate: safeText(duty?.date),
          day: safeText(duty?.day || dutyIndex + 1),
          service: safeText(service?.name || service?.service || `Service ${serviceIndex + 1}`),
          reportedAt: formatDate(service?.dateTime, true),
          details: safeText(service?.values ?? service?.value),
        });
      });
    });
    asArray(visit?.timeline).forEach((item) => {
      const actorName = [item?.actor?.name, item?.actor?.role]
        .map((part) => String(part || "").trim())
        .filter(Boolean)
        .join(" / ");
      timelineRows.push({
        bookingId,
        visitId,
        patientName,
        event: safeText(item?.event),
        when: formatDate(item?.dateTime, true),
        description: safeText(item?.description),
        actor: safeText(actorName),
      });
    });
  });

  const members = asArray(careTeam).filter((member) => member?.role !== "patient");
  const patientAccounts = asArray(patients).filter((patient) => patient?.role === "patient");
  const summary = bookingExport.summarizeDetailedBookingExport(exportBookings);
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Ceviliyar";
  workbook.created = new Date();

  addSummarySheet(
    workbook,
    "Ceviliyar Admin Partner Data Export",
    [
      ["Generated At (IST)", formatDate(new Date(), true)],
      ["Partner", safeText(partner?.basicInfo?.name)],
      ["Partner ID", safeText(partner?._id)],
      ["Care Team Members", members.length],
      ["Patient Accounts", patientAccounts.length],
      ["Exportable Bookings", exportBookings.length],
      ["Duty Days", summary.totalDutyDays],
      ["Total Patient Payment", safeText(summary.patientReceived)],
      ["Ceviliyar Platform Fees", safeText(summary.companyPayout)],
      ["Total Partner Payout", safeText(summary.totalPayout)],
      ["Processed Transfers", safeText(summary.transferredPayout)],
      ["Pending Transfers", safeText(summary.pendingPayout)],
      ["Missing Payout Duties", summary.missingPayouts],
      ["Not Yet Eligible Duties", summary.notYetEligible],
    ],
    [],
  );

  const careTeamRows = members.map((member) => {
    const otherInfo = getOtherInfo(member);
    return {
      name: getName(member),
      role: safeText(member?.subRole || member?.role),
      active: safeText(member?.active),
      phone: safeText(member?.phone || otherInfo?.phone),
      email: safeText(otherInfo?.email),
      gender: safeText(otherInfo?.gender),
      dob: formatDate(otherInfo?.dob),
      address: formatAddress(member?.personalInfo?.addressInfo),
      onboardedOn: formatDate(member?.createdAt, true),
    };
  });
  addSheet(
    workbook,
    "Care Team Master",
    [
      ...CARE_TEAM_BASE_COLUMNS,
      { header: "Active", key: "active", width: 11 },
      { header: "Phone", key: "phone", width: 16 },
      { header: "Email", key: "email", width: 28 },
      { header: "Gender", key: "gender", width: 12 },
      { header: "DOB", key: "dob", width: 14 },
      { header: "Address", key: "address", width: 45 },
      { header: "Onboarded On", key: "onboardedOn", width: 20 },
    ],
    careTeamRows,
  );

  const patientRows = patientAccounts.map((account) => {
    const otherInfo = getOtherInfo(account);
    const contact = getContactPerson(account);
    return {
      mrn: safeText(account?.mrn),
      name: getName(account),
      gender: safeText(otherInfo?.gender),
      dob: formatDate(otherInfo?.dob),
      phone: safeText(account?.phone || otherInfo?.phone),
      address: formatAddress(account?.personalInfo?.addressInfo),
      contactPerson: contact.name,
      contactPhone: contact.phone,
      createdAt: formatDate(account?.createdAt, true),
    };
  });
  addSheet(workbook, "Patient Master", PATIENT_MASTER_COLUMNS, patientRows);
  addSheet(workbook, "Booking Master", ADMIN_BOOKING_COLUMNS, bookingRows);
  addSheet(workbook, "Booking Duties", ADMIN_DUTY_COLUMNS, dutyRows);
  addSheet(workbook, "Booking Services", ADMIN_SERVICE_COLUMNS, serviceRows);
  addSheet(workbook, "Booking Timeline", ADMIN_TIMELINE_COLUMNS, timelineRows);

  const fileName = `${normalizeFilePart(sourceLabel, "admin-partner")}-data-${moment().format(
    "YYYYMMDD-HHmmss",
  )}.xlsx`;
  await saveWorkbook(workbook, fileName);
  return {
    careTeamCount: members.length,
    patientCount: patientAccounts.length,
    bookingCount: exportBookings.length,
    dutyCount: dutyRows.length,
    fileName,
  };
};
